import React, { useCallback, useMemo } from 'react';

import './customization-page.css';

import { Modality, ModalityDirection } from '../../store/global-types/customization-types';
import { sortEnumsInIndexOrder } from '../../util/enum';
import ModelTag, { ModelTagStyle } from '../../components/model-tag';

interface ModelModalitiesProps {
    modalities: Modality[];
    direction: ModalityDirection;
    label: string;
    onToggle?: (modality: Modality, direction: ModalityDirection) => void;
    selectedModalities?: Modality[];
    inline?: boolean;
}

const ModelModalities = (props: ModelModalitiesProps) => {
    const { modalities, direction, label, onToggle, selectedModalities, inline = true } = props;

    // onToggle prop must be given for this component to be interactable
    // (selectedModalities prop is optional)
    const isInteractable = !!onToggle;

    const handleClick = useCallback((modality: Modality) => {
        if (onToggle) {
            onToggle(modality, direction);
        }
    }, [onToggle, direction]);

    // Memoize selectedModalities as a Set for O(1) lookup performance
    const selectedModalitiesSet = useMemo(() => 
        selectedModalities ? new Set(selectedModalities) : new Set<Modality>(), 
        [selectedModalities]
    );

    return (
        <span className="model-modalities">
            <span className="model-modalities-label">
                {label}:
            </span>
            <span className={`${inline ? '' : 'model-modalities-block'}`}>
                {sortEnumsInIndexOrder(Modality, modalities).map((m, i) => {
                    const isModalitySelected = selectedModalitiesSet.has(m);
                    return (
                        <span key={m} className="model-tag-container">
                            <ModelTag
                                allowTextSelect={isInteractable}
                                allowInteraction={isInteractable}
                                style={ModelTagStyle.Highlighted}
                                isActive={isModalitySelected}
                                onClick={isInteractable ? () => handleClick(m) : undefined}
                                testId={isInteractable ? `model-filter-${direction === ModalityDirection.Input ? 'input' : 'output'}-modality-badge` : undefined}
                            >
                                {m}
                            </ModelTag>
                            {i < modalities.length - 1 ? <span className="model-modality-separator"> | </span> : ''}
                        </span>
                    );
                })}
            </span>
        </span>
    );
}

export default ModelModalities;
